import React, { useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, IconButton } from '@mui/material';
import { GridRowId, GridSortModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import ContractTable from './ContractTable';
import CreateContractType from './CreateContractType';
import { deleteContract, getDictionary } from '../../axiosClient/dictionaryApi';
import DeleteIcon from '../../Icons/DeleteIcon';
import { IObject } from '../../interfaces/IObject';

const initialFilter = {
  contract: null,
  assignees: null,
};

const ContractComponent = () => {
  const [filters, setFilters] = useState<IObject>(initialFilter);
  const [sorting, setSorting] = useState<GridSortModel>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<GridRowId[]>([]);
  const [contractTableKey, setContractTableKey] = useState<number>(0);
  const [openAddContractType, setOpenAddContractType] =
    useState<boolean>(false);

  const queryClient = useQueryClient();

  const { mutate: deleteContractMutation } = useMutation({
    mutationFn: deleteContract,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['contracts'],
      });
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', pageNumber, filters, sorting, contractTableKey],
    queryFn: async () => {
      let filterParam: string = '';
      for (const key in filters) {
        if (filters[key]) {
          filterParam = `${filterParam}&${key}=${filters[key]}`;
        }
      }
      let sortingParam: string = '';

      if (sorting.length > 0) {
        for (let i = 0; i < sorting.length; i++) {
          if (sorting[i].sort === 'asc') {
            sortingParam = `${sortingParam}&ordering=${sorting[i].field}`;
          } else {
            sortingParam = `${sortingParam}&ordering=-${sorting[i].field}`;
          }
        }
      }
      let params: string = '';
      const page = pageNumber + 1;
      params = `${'?page=' + page}${sortingParam}${filterParam}`;
      const response = await getDictionary(params);
      return response;
    },
  });

  const handleDelete = React.useCallback(
    (ids: GridRowId[]) => {
      if (ids.length > 0) {
        deleteContractMutation(ids);
      }
    },
    [deleteContractMutation]
  );

  return (
    <Box>
      <Button
        variant="contained"
        sx={{
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '11px',
          width: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1,
          alignItems: 'center',

          '&:hover': {
            backgroundColor: 'riPrimary.500',
          },
        }}
        onClick={() => setOpenAddContractType(true)}
      >
        <UploadFileIcon fontSize="small" />
        <br />
        Add new contract type
      </Button>
      {selectedRow.length ? (
        <IconButton
          aria-label="delete"
          color="primary"
          onClick={() => handleDelete(selectedRow)}
        >
          <DeleteIcon />
        </IconButton>
      ) : (
        <IconButton onClick={() => setContractTableKey(Math.random())}>
          <RefreshIcon />
        </IconButton>
      )}
      <ContractTable
        data={data}
        isLoading={isLoading}
        pageNumber={pageNumber}
        setPageNumberChange={setPageNumber}
        setFilters={setFilters}
        setSorting={setSorting}
        filters={filters}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
      />
      {openAddContractType && (
        <CreateContractType
          open={openAddContractType}
          handleClose={() => setOpenAddContractType(false)}
        />
      )}
    </Box>
  );
};

export default ContractComponent;
